import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Link as LinkIcon, FileText, Wrench, BookOpen,
  AlertCircle, CheckCircle2, ExternalLink,
} from 'lucide-react';
import { api } from '@/utils/api';

// Admin page: set the three doc references per product.
// Minimalist by design — Norman: don't invent a doc CMS when the backend
// model is "two URLs and a KB category id".

interface ProductLite {
  id: string;
  name: string;
  sku?: string;
  installationGuideUrl?: string | null;
  serviceManualUrl?: string | null;
  kbCategoryId?: string | null;
}

interface KBCategoryLite {
  id: string;
  name: string;
}

export function ProductDocsAdminPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [product, setProduct] = useState<ProductLite | null>(null);
  const [kbCategories, setKbCategories] = useState<KBCategoryLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const [form, setForm] = useState({
    installationGuideUrl: '',
    serviceManualUrl: '',
    kbCategoryId: '',
  });

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [pRes, kbRes] = await Promise.all([
        api.get(`/products/${id}`),
        api.get('/knowledge-base/categories').catch(() => ({ data: { data: [] } })),
      ]);
      const p: ProductLite = pRes.data.data?.product ?? pRes.data.data ?? pRes.data;
      setProduct(p);
      setForm({
        installationGuideUrl: p.installationGuideUrl || '',
        serviceManualUrl: p.serviceManualUrl || '',
        kbCategoryId: p.kbCategoryId || '',
      });
      const cats = kbRes.data.data?.categories ?? kbRes.data.data ?? [];
      setKbCategories(Array.isArray(cats) ? cats : []);
    } catch (err) {
      const errMsg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Failed to load product';
      setMsg({ kind: 'err', text: errMsg });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const validateUrl = (v: string): string | null => {
    if (!v) return null;
    try {
      const u = new URL(v);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return 'Must be http:// or https://';
      return null;
    } catch {
      return 'Not a valid URL';
    }
  };

  const installErr = validateUrl(form.installationGuideUrl);
  const serviceErr = validateUrl(form.serviceManualUrl);
  const hasError = !!installErr || !!serviceErr;

  const handleSave = async () => {
    if (!id || hasError) return;
    setSaving(true);
    setMsg(null);
    try {
      await api.patch(`/products/${id}/docs`, {
        installationGuideUrl: form.installationGuideUrl || null,
        serviceManualUrl: form.serviceManualUrl || null,
        kbCategoryId: form.kbCategoryId || null,
      });
      setMsg({ kind: 'ok', text: 'Saved.' });
    } catch (err) {
      // Fall back to generic product PATCH if /docs endpoint is not yet live
      try {
        await api.patch(`/products/${id}`, {
          installationGuideUrl: form.installationGuideUrl || null,
          serviceManualUrl: form.serviceManualUrl || null,
          kbCategoryId: form.kbCategoryId || null,
        });
        setMsg({ kind: 'ok', text: 'Saved.' });
      } catch (err2) {
        const errMsg = ((err2 as { response?: { data?: { error?: string } } }).response?.data?.error)
          || ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
          || 'Save failed';
        setMsg({ kind: 'err', text: errMsg });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-xl rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Product not found.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Product Documentation</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {product.name} {product.sku && `· ${product.sku}`}
          </p>
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900">
        <UrlField
          icon={<FileText size={16} className="text-blue-500" />}
          label="Installation guide URL"
          helper="PDF or video link. Agents open this on-site before installation."
          value={form.installationGuideUrl}
          onChange={v => setForm(f => ({ ...f, installationGuideUrl: v }))}
          error={installErr}
        />
        <UrlField
          icon={<Wrench size={16} className="text-amber-500" />}
          label="Service manual URL"
          helper="Repair, diagnosis, parts list. Agents open this for service visits."
          value={form.serviceManualUrl}
          onChange={v => setForm(f => ({ ...f, serviceManualUrl: v }))}
          error={serviceErr}
        />

        <div>
          <label className="mb-1 flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
            <BookOpen size={16} className="text-green-500" />
            Knowledge base category
          </label>
          <select
            value={form.kbCategoryId}
            onChange={e => setForm(f => ({ ...f, kbCategoryId: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-2 focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">None</option>
            {kbCategories.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Link a KB category for video walkthroughs, FAQs, troubleshooting.
          </p>
        </div>

        {msg && (
          <div
            className={`flex items-start gap-2 rounded-md border p-2 text-xs ${
              msg.kind === 'ok'
                ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300'
                : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300'
            }`}
          >
            {msg.kind === 'ok' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
            {msg.text}
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || hasError}
          className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--ink-1)] disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
          Save docs
        </button>
      </div>
    </div>
  );
}

function UrlField({
  icon,
  label,
  helper,
  value,
  onChange,
  error,
}: {
  icon: React.ReactNode;
  label: string;
  helper: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | null;
}) {
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
        {icon}
        {label}
      </label>
      <div className="flex">
        <span className="flex items-center rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 px-2 text-gray-400 dark:border-gray-700 dark:bg-gray-800">
          <LinkIcon size={14} />
        </span>
        <input
          type="url"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="https://…"
          className={`w-full rounded-r-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
            error
              ? 'border-red-400 focus:ring-red-200'
              : 'border-gray-300 focus:border-[var(--ink-0)] focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100'
          }`}
        />
        {value && !error && (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 flex items-center rounded-lg border border-gray-300 px-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label="Open in new tab"
          >
            <ExternalLink size={14} />
          </a>
        )}
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helper}</p>
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
